import type { ComponentType, ReactNode } from 'react';
import {
  Badge,
  Button,
  cn,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { JobConfirmDialog } from '@/features/jobs/jobs-list/JobConfirmDialog';
import { JobHeaderLogo } from '@/features/jobs/job-shared/JobHeaderLogo';
import type { JobSectionId } from '@/features/jobs/job-shared/job-sections';
import { BanIcon, FilePenIcon, FlagIcon, InfoIcon, RadioIcon } from '../job-shared/icons';
import {
  ActivityIcon,
  ArrowUpRightIcon,
  CircleXIcon,
  DownloadIcon,
  MoveHorizontalIcon,
} from './icons';

export type JobStatus = 'ativa' | 'ativa-privada' | 'rascunho' | 'encerrada' | 'cancelada';

export type JobManagementSectionId = JobSectionId;

export interface JobManagementSection {
  id: JobManagementSectionId;
  title: string;
  /** Status de preenchimento da seção — "Preenchido", contadores
   *  ("2 fases · 3 etapas") ou "Seção ainda não preenchida" (mesma fonte do
   *  antigo hub de edição: `buildJobSections`). */
  description: string;
  optional?: boolean;
}

/** Ações de ciclo de vida do card de resumo (RN-01..04). */
export type JobLifecycleAction = 'duplicate' | 'end' | 'cancel';

/** Ações operacionais de candidatos da grade (RN-07..10). */
export type JobOperationalAction = 'export' | 'numbers' | 'move' | 'reject';

export type JobManagementDialog = 'end' | 'cancel' | null;

export interface JobManagementPageProps {
  status: JobStatus;
  name: string;
  programName: string;
  /** Janela de inscrição do PROGRAMA vinculado, já formatada (EU-427 — a vaga
   *  não tem datas próprias). `null` esconde a linha do prazo. */
  deadline: { start: string; end: string } | null;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  sections: JobManagementSection[];
  onLifecycleAction: (action: JobLifecycleAction) => void;
  onOperationalAction: (action: JobOperationalAction) => void;
  onSectionOpen: (id: JobManagementSectionId) => void;
  /** Rodapé — volta para a listagem de vagas. */
  onBack: () => void;
  activeDialog: JobManagementDialog;
  onDialogOpenChange: (open: boolean) => void;
  onConfirmEnd: () => void;
  onConfirmCancel: () => void;
  isActionInFlight?: boolean;
}

/** RN-07 — dashboard de inscritos ainda não lançado. */
const NUMBERS_TOOLTIP = 'Em breve';
/** RN-02/04 — rascunho/cancelada não têm candidatos vinculados. */
const NO_CANDIDATES_TOOLTIP = 'Sem candidatos';

export function JobManagementPage({
  status,
  name,
  programName,
  deadline,
  companyLogoUrl,
  companyName,
  sections,
  onLifecycleAction,
  onOperationalAction,
  onSectionOpen,
  onBack,
  activeDialog,
  onDialogOpenChange,
  onConfirmEnd,
  onConfirmCancel,
  isActionInFlight = false,
}: JobManagementPageProps) {
  // RN-01..04 — matriz de ações por status.
  const isActive = status === 'ativa' || status === 'ativa-privada';
  const canEnd = isActive;
  const canCancel = isActive || status === 'rascunho';
  // RN-03 — encerrada mantém as operacionais (ajustes pós-encerramento).
  const operationalEnabled = isActive || status === 'encerrada';
  // Figma: a vaga cancelada não exibe a linha do prazo.
  const showDeadline = deadline !== null && status !== 'cancelada';

  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />

      <main className="flex flex-1 flex-col items-center bg-card px-4 pt-8 pb-16 md:px-8">
        <div className="flex w-full max-w-[1280px] flex-col gap-8">
          <header className="flex flex-row items-center gap-3">
            <JobHeaderLogo logoUrl={companyLogoUrl} companyName={companyName} />
            <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
              Gestão de vaga
            </h1>
          </header>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Coluna esquerda — card de resumo + grade de ações operacionais. */}
            <div className="flex flex-col gap-6">
              <section className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-xs">
                <div>
                  <StatusBadge status={status} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <h2 className="text-2xl font-medium leading-tight tracking-tight text-foreground">
                    {name}
                  </h2>
                  <p className="text-base leading-snug text-muted-foreground">{programName}</p>
                </div>
                {showDeadline ? (
                  <p
                    data-testid="job-management-deadline"
                    className="text-sm text-muted-foreground"
                  >
                    Prazo de inscrição:{' '}
                    <span className="font-medium text-foreground">{deadline.start}</span> até{' '}
                    <span className="font-medium text-foreground">{deadline.end}</span>
                  </p>
                ) : null}
                <div className="flex flex-row items-center gap-2 pt-1">
                  <Button
                    data-testid="job-management-duplicate"
                    onClick={() => onLifecycleAction('duplicate')}
                    disabled={isActionInFlight}
                  >
                    Duplicar vaga
                  </Button>
                  {canEnd ? (
                    <LifecycleIconButton
                      testId="job-management-end"
                      label="Encerrar vaga"
                      Icon={FlagIcon}
                      onClick={() => onLifecycleAction('end')}
                      disabled={isActionInFlight}
                    />
                  ) : null}
                  {canCancel ? (
                    <LifecycleIconButton
                      testId="job-management-cancel"
                      label="Cancelar vaga"
                      Icon={BanIcon}
                      onClick={() => onLifecycleAction('cancel')}
                      disabled={isActionInFlight}
                    />
                  ) : null}
                </div>
              </section>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <ActionCard
                  testId="job-management-export"
                  label="Exportar candidatos"
                  Icon={DownloadIcon}
                  enabled={operationalEnabled}
                  disabledTooltip={NO_CANDIDATES_TOOLTIP}
                  onClick={() => onOperationalAction('export')}
                />
                <ActionCard
                  testId="job-management-numbers"
                  label="Conferir números"
                  Icon={ActivityIcon}
                  enabled={false}
                  disabledTooltip={NUMBERS_TOOLTIP}
                  onClick={() => onOperationalAction('numbers')}
                />
                <ActionCard
                  testId="job-management-move"
                  label="Movimentar candidatos"
                  Icon={MoveHorizontalIcon}
                  enabled={operationalEnabled}
                  disabledTooltip={NO_CANDIDATES_TOOLTIP}
                  onClick={() => onOperationalAction('move')}
                />
                <ActionCard
                  testId="job-management-reject"
                  label="Reprovar candidatos"
                  Icon={CircleXIcon}
                  enabled={operationalEnabled}
                  disabledTooltip={NO_CANDIDATES_TOOLTIP}
                  onClick={() => onOperationalAction('reject')}
                />
              </div>
            </div>

            {/* Coluna direita — painel "Dados" (RN-11, disponível em todo status). */}
            <section
              data-testid="job-management-data-panel"
              className="flex h-fit flex-col gap-2 rounded-xl border border-border bg-card p-6 shadow-xs"
            >
              <div className="flex flex-row items-center gap-3 pb-2">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                  <InfoIcon className="size-4" />
                </span>
                <h2 className="text-2xl font-medium leading-none tracking-tight text-foreground">
                  Dados
                </h2>
              </div>
              <ul className="flex flex-col">
                {sections.map((section, index) => (
                  <li key={section.id}>
                    {index > 0 ? <div aria-hidden className="h-px w-full bg-border" /> : null}
                    <button
                      type="button"
                      data-testid={`job-management-section-${section.id}`}
                      onClick={() => onSectionOpen(section.id)}
                      className={cn(
                        'group flex w-full flex-row items-center gap-3 py-5 text-left',
                        'rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background'
                      )}
                    >
                      <span className="flex flex-1 flex-col gap-1.5">
                        <span
                          data-section-title
                          className="text-base font-medium leading-none text-accent-foreground"
                        >
                          {section.title}
                        </span>
                        <span
                          data-testid={`job-management-section-${section.id}-status`}
                          className="text-xs leading-none text-muted-foreground"
                        >
                          {section.description}
                        </span>
                      </span>
                      {section.optional ? (
                        <span
                          data-testid={`job-management-section-${section.id}-optional`}
                          className="text-xs leading-none text-muted-foreground"
                        >
                          Opcional
                        </span>
                      ) : null}
                      <ArrowUpRightIcon className="size-5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <footer className="flex flex-row pt-4">
            <Button
              variant="outline"
              size="lg"
              data-testid="job-management-return"
              onClick={onBack}
            >
              Voltar
            </Button>
          </footer>
        </div>
      </main>

      {/* RN-05 — modal de confirmação do encerramento. */}
      <JobConfirmDialog
        open={activeDialog === 'end'}
        onOpenChange={onDialogOpenChange}
        title="Deseja encerrar essa Vaga?"
        description="Ela deixará de receber novas candidaturas e não poderá ser reaberta."
        confirmLabel={isActionInFlight ? 'Encerrando...' : 'Confirmar encerramento'}
        onConfirm={onConfirmEnd}
      />

      {/* RN-06 — modal de confirmação do cancelamento. */}
      <JobConfirmDialog
        open={activeDialog === 'cancel'}
        onOpenChange={onDialogOpenChange}
        title="Deseja cancelar essa Vaga?"
        description="Ela deixará de receber novas candidaturas e não poderá ser reaberta."
        confirmLabel={isActionInFlight ? 'Cancelando...' : 'Confirmar cancelamento'}
        onConfirm={onConfirmCancel}
      />
    </div>
  );
}

interface LifecycleIconButtonProps {
  testId: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
}

function LifecycleIconButton({ testId, label, Icon, onClick, disabled }: LifecycleIconButtonProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex size-9 items-center justify-center rounded-md border border-input bg-card text-foreground',
        'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        'disabled:pointer-events-none disabled:opacity-50'
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}

interface ActionCardProps {
  testId: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  enabled: boolean;
  disabledTooltip: string;
  onClick: () => void;
}

function ActionCardBody({
  label,
  Icon,
  enabled,
}: Pick<ActionCardProps, 'label' | 'Icon'> & { enabled: boolean }) {
  return (
    <>
      <span
        className={cn(
          'flex size-9 items-center justify-center rounded-md bg-secondary text-primary',
          !enabled && 'opacity-50'
        )}
      >
        <Icon className="size-4" />
      </span>
      <span
        className={cn(
          'text-base font-medium leading-none',
          enabled ? 'text-accent-foreground' : 'text-muted-foreground/70'
        )}
      >
        {label}
      </span>
    </>
  );
}

const ACTION_CARD_CLASS = cn(
  'flex min-h-[176px] w-full flex-col items-start justify-between gap-10 rounded-xl border border-border bg-card p-5 text-left shadow-xs'
);

/**
 * Card da grade de ações operacionais. Radix Tooltip não dispara em botão
 * `disabled`, então o card bloqueado é envolvido por um `<span>` focável que
 * hospeda o tooltip — mesmo padrão do gate de Forms (`FormsListPage`,
 * `forms-list-new-disabled-wrapper`).
 */
function ActionCard({ testId, label, Icon, enabled, disabledTooltip, onClick }: ActionCardProps) {
  if (enabled) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={cn(
          ACTION_CARD_CLASS,
          'transition-colors hover:border-primary/40 hover:bg-muted/40',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
        )}
      >
        <ActionCardBody label={label} Icon={Icon} enabled />
      </button>
    );
  }
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            data-testid={`${testId}-disabled-wrapper`}
            className="cursor-not-allowed"
          >
            <button type="button" data-testid={testId} disabled className={ACTION_CARD_CLASS}>
              <ActionCardBody label={label} Icon={Icon} enabled={false} />
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function StatusBadge({ status }: { status: JobStatus }): ReactNode {
  if (status === 'ativa') {
    return (
      <Badge className="bg-secondary text-primary" leftIcon={<RadioIcon className="size-3" />}>
        Ativa
      </Badge>
    );
  }
  if (status === 'ativa-privada') {
    return (
      <Badge className="bg-secondary text-primary" leftIcon={<RadioIcon className="size-3" />}>
        Ativa e priv.
      </Badge>
    );
  }
  if (status === 'rascunho') {
    return (
      <Badge
        className="bg-warning text-primary-foreground"
        leftIcon={<FilePenIcon className="size-3" />}
      >
        Rascunho
      </Badge>
    );
  }
  if (status === 'encerrada') {
    return (
      <Badge
        className="bg-primary text-primary-foreground"
        leftIcon={<FlagIcon className="size-3" />}
      >
        Encerrada
      </Badge>
    );
  }
  return (
    <Badge
      className="bg-destructive text-primary-foreground"
      leftIcon={<BanIcon className="size-3" />}
    >
      Cancelada
    </Badge>
  );
}
